import { useEffect, useState } from 'react';
import { getUserUsage } from '../../../services/UsersService';
import { formatLimit, isUnlimited } from '../../../utils/planLimits';

function formatDate(value) {
    return value ? new Date(value).toLocaleString() : '-';
}

function UsageBar({ label, usage, suffix }) {
    const used = usage ? usage.used : 0;
    const max = usage ? usage.max : 0;

    let percent = 0;
    if (isUnlimited(max)) percent = 0;
    else if (max > 0) percent = Math.min(100, Math.round((used / max) * 100));
    else if (used > 0) percent = 100;

    let color = 'bg-success';
    if (percent >= 100) color = 'bg-danger';
    else if (percent >= 80) color = 'bg-warning';

    return (
        <div className="mb-3">
            <div className="d-flex justify-content-between small mb-1">
                <span>
                    {label}
                    {suffix ? ` (${suffix})` : ''}
                </span>
                <span>{usage ? `${used} / ${formatLimit(max)}` : '...'}</span>
            </div>
            <div className="progress">
                <div
                    className={`progress-bar ${color}`}
                    role="progressbar"
                    style={{ width: `${percent}%` }}
                    aria-valuenow={used}
                    aria-valuemin="0"
                    aria-valuemax={max}
                ></div>
            </div>
        </div>
    );
}

/**
 * props:
 * - data
 */
function UserInfoModal(props) {
    const user = props.data || {};

    const [usage, setUsage] = useState(null);
    const [error, setError] = useState('');

    useEffect(() => {
        setUsage(null);
        setError('');
        if (!user.id) return;

        getUserUsage(user.id)
            .then(result => setUsage(result))
            .catch(err => {
                console.error(err.response ? err.response.data : err.message);
                setError(err.response ? err.response.data : err.message);
            });
    }, [user.id]);

    return (
        <div
            className="modal fade"
            id="modalUserInfo"
            tabIndex="-1"
            role="dialog"
            aria-labelledby="modalTitleUserInfo"
            aria-hidden="true"
        >
            <div
                className="modal-dialog modal-dialog-centered modal-lg"
                role="document"
            >
                <div className="modal-content">
                    <div className="modal-header">
                        <p className="modal-title" id="modalTitleUserInfo">
                            User Details
                        </p>
                        <button
                            type="button"
                            className="btn-close"
                            data-bs-dismiss="modal"
                            aria-label="close"
                        ></button>
                    </div>
                    <div className="modal-body">
                        <div className="row mb-3">
                            <div className="col-12 mb-2">
                                <strong>{user.name}</strong>{' '}
                                {user.isActive ? (
                                    <span className="badge bg-success">
                                        Active
                                    </span>
                                ) : (
                                    <span className="badge bg-danger">
                                        Inactive
                                    </span>
                                )}
                            </div>
                            <div className="col-md-6 mb-2">
                                <div className="small text-muted">Email</div>
                                <div>{user.email || '-'}</div>
                            </div>
                            <div className="col-md-6 mb-2">
                                <div className="small text-muted">
                                    Cellphone
                                </div>
                                <div>{user.phone || '-'}</div>
                            </div>
                            <div className="col-md-6 mb-2">
                                <div className="small text-muted">
                                    Telegram Chat ID
                                </div>
                                <div>{user.telegramChat || '-'}</div>
                            </div>
                            <div className="col-md-6 mb-2">
                                <div className="small text-muted">Created</div>
                                <div>{formatDate(user.createdAt)}</div>
                            </div>
                        </div>
                        <hr />
                        <div className="row mb-3">
                            <div className="col-md-6 mb-2">
                                <div className="small text-muted">Plan</div>
                                <div>
                                    {user.plan ? user.plan.name : '-'}{' '}
                                    {user.plan && user.plan.isTrial ? (
                                        <span className="badge bg-info">
                                            Trial
                                        </span>
                                    ) : (
                                        <></>
                                    )}
                                </div>
                            </div>
                            <div className="col-md-6 mb-2">
                                <div className="small text-muted">
                                    Trial expires
                                </div>
                                <div
                                    className={
                                        user.trialExpiresAt &&
                                        new Date(user.trialExpiresAt) <=
                                            new Date()
                                            ? 'text-danger'
                                            : ''
                                    }
                                >
                                    {formatDate(user.trialExpiresAt)}
                                </div>
                            </div>
                            <div className="col-md-6 mb-2">
                                <div className="small text-muted">Futures</div>
                                <div>
                                    {usage
                                        ? usage.hasFutures
                                            ? 'Yes'
                                            : 'No'
                                        : '...'}
                                </div>
                            </div>
                            <div className="col-md-6 mb-2">
                                <div className="small text-muted">Launch</div>
                                <div>
                                    {usage
                                        ? usage.hasLaunch
                                            ? 'Yes'
                                            : 'No'
                                        : '...'}
                                </div>
                            </div>
                            <div className="col-md-6 mb-2">
                                <div className="small text-muted">
                                    Strategies
                                </div>
                                <div>
                                    {usage
                                        ? usage.hasStrategies
                                            ? 'Yes'
                                            : 'No'
                                        : '...'}
                                </div>
                            </div>
                        </div>
                        <hr />
                        <p className="small text-muted mb-2">Plan usage</p>
                        <UsageBar
                            label="Automations"
                            usage={usage && usage.automations}
                        />
                        <UsageBar
                            label="Monitors"
                            usage={usage && usage.monitors}
                        />
                        <UsageBar
                            label="Backtests"
                            suffix="this month"
                            usage={usage && usage.backtests}
                        />
                    </div>
                    {error ? (
                        <div className="modal-footer">
                            <div className="alert alert-danger mt-1 col-12 py-1">
                                {error}
                            </div>
                        </div>
                    ) : (
                        <></>
                    )}
                </div>
            </div>
        </div>
    );
}

export default UserInfoModal;
